/**
 * src/views/ApprovalsView.tsx
 * Approvals Inbox for Admins and My Submissions View for Regular Users
 */

import React, { useState } from 'react';
import { 
  Inbox, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Plus, 
  Search, 
  RefreshCw,
  ShieldAlert,
  SlidersHorizontal,
  Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useApprovals } from '../hooks/useApprovals';
import { GlassCard } from '../components/ui/GlassCard';
import { GlassButton } from '../components/ui/GlassButton';
import { ApprovalCard } from '../components/approvals/ApprovalCard';
import { RejectReasonModal } from '../components/approvals/RejectReasonModal';
import { UserSubmissionModal } from '../components/approvals/UserSubmissionModal';
import type { CategoryItem } from '../services/api';

interface ApprovalsViewProps {
  categories: CategoryItem[];
}

export const ApprovalsView: React.FC<ApprovalsViewProps> = ({ categories }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const {
    requests,
    pendingCount,
    loading,
    statusFilter,
    setStatusFilter,
    refresh,
    submitRequest,
    reviewRequest,
  } = useApprovals('pending');

  const [searchQuery, setSearchQuery] = useState('');
  const [rejectingItem, setRejectingItem] = useState<{ id: number; title: string } | null>(null);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [processingId, setProcessingId] = useState<number | null>(null);

  // Filter requests locally by search query
  const filteredRequests = requests.filter(req => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      req.description.toLowerCase().includes(q) ||
      req.submitter_name?.toLowerCase().includes(q) ||
      (req.category_name && req.category_name.toLowerCase().includes(q)) ||
      String(req.amount).includes(q)
    );
  });

  const handleApprove = async (id: number) => {
    setProcessingId(id);
    try {
      await reviewRequest(id, 'approve');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectPrompt = (id: number) => {
    const item = requests.find(r => r.id === id);
    setRejectingItem({
      id,
      title: item ? `${item.submitter_name} — ₹${item.amount} (${item.description})` : ''
    });
  };

  const handleConfirmReject = async (reason: string) => {
    if (!rejectingItem) return;
    setProcessingId(rejectingItem.id);
    try {
      await reviewRequest(rejectingItem.id, 'reject', reason);
      setRejectingItem(null);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingBottom: '3rem' }}>
      {/* Top Banner / Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.025em',
              margin: 0
            }}>
              {isAdmin ? 'Approvals Inbox' : 'My Requests'}
            </h1>
            {isAdmin && pendingCount > 0 && (
              <span style={{
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff',
                fontSize: '0.8125rem',
                fontWeight: 800,
                padding: '0.2rem 0.65rem',
                borderRadius: 'var(--radius-full)',
                boxShadow: '0 0 12px rgba(245, 158, 11, 0.4)'
              }}>
                {pendingCount} Under Review
              </span>
            )}
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0 0' }}>
            {isAdmin 
              ? 'Verify and approve member transactions before they commit to the ledger.'
              : 'Track transaction entries awaiting Admin verification and balance updates.'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <GlassButton variant="ghost" onClick={refresh} title="Refresh requests">
            <RefreshCw size={16} className={loading ? 'spin-slow' : ''} />
          </GlassButton>

          {!isAdmin && (
            <GlassButton variant="primary" onClick={() => setIsSubmitModalOpen(true)}>
              <Plus size={16} />
              New Entry
            </GlassButton>
          )}
        </div>
      </div>

      {/* Tabs & Search Controls - iOS Inset Group */}
      <div className="ios-inset-group" style={{ padding: '12px 16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          {/* iOS Status Segmented Capsule */}
          <div className="ios-segmented-control" style={{ padding: '3px' }}>
            {[
              { id: 'pending', label: 'Under Review', count: pendingCount, icon: Clock },
              { id: 'approved', label: 'Approved', icon: CheckCircle2 },
              { id: 'rejected', label: 'Rejected', icon: XCircle },
              { id: 'all', label: 'All History', icon: SlidersHorizontal },
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setStatusFilter(tab.id)}
                  className={`ios-segment-button ${isActive ? 'active' : ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 12px',
                    fontSize: '0.78rem'
                  }}
                >
                  <Icon size={13} />
                  <span>{tab.label}</span>
                  {tab.id === 'pending' && tab.count !== undefined && tab.count > 0 && (
                    <span style={{
                      background: 'var(--ios-orange)',
                      color: '#ffffff',
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-pill)',
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      marginLeft: '2px'
                    }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* iOS Quick Search */}
          <div style={{ position: 'relative', minWidth: '220px', flex: '1 1 220px' }}>
            <Search size={15} style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-tertiary)',
              pointerEvents: 'none'
            }} />
            <input
              type="text"
              placeholder="Search description, member..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="glass-input"
              style={{
                paddingLeft: '36px',
                paddingRight: '12px',
                paddingTop: '7px',
                paddingBottom: '7px',
                fontSize: '0.8125rem',
                borderRadius: '10px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Requests Deck List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem 1rem' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            color: 'var(--text-tertiary)'
          }}>
            <RefreshCw size={28} className="animate-spin" style={{ animation: 'spin 1s linear infinite', color: 'var(--ios-blue)' }} />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>Loading approval requests...</p>
          </div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="ios-inset-group" style={{ padding: '3.5rem 1.5rem', textAlign: 'center' }}>
          <div className="ios-icon-squircle blue" style={{ width: '48px', height: '48px', margin: '0 auto 1.25rem auto' }}>
            {statusFilter === 'pending' ? <CheckCircle2 size={24} /> : <Inbox size={24} />}
          </div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem 0', letterSpacing: '-0.015em' }}>
            {statusFilter === 'pending' 
              ? (isAdmin ? 'All caught up!' : 'No pending requests')
              : 'No requests found'}
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', maxWidth: '380px', margin: '0 auto 1.5rem auto' }}>
            {statusFilter === 'pending'
              ? (isAdmin ? 'There are no pending submissions requiring your review at this moment.' : 'You have no entries waiting for review. Ready to submit a new entry?')
              : `No requests with status "${statusFilter}" were found matching your filters.`}
          </p>

          {!isAdmin && (
            <GlassButton variant="primary" onClick={() => setIsSubmitModalOpen(true)}>
              <Plus size={16} />
              Submit First Entry
            </GlassButton>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredRequests.map(req => (
            <ApprovalCard
              key={req.id}
              item={req}
              isAdmin={isAdmin}
              onApprove={handleApprove}
              onReject={handleRejectPrompt}
              isProcessing={processingId === req.id}
            />
          ))}
        </div>
      )}



      {/* Reject Reason Dialog */}
      <RejectReasonModal
        isOpen={Boolean(rejectingItem)}
        onClose={() => setRejectingItem(null)}
        onConfirm={handleConfirmReject}
        requestTitle={rejectingItem?.title}
      />

      {/* User Submission Dialog */}
      <UserSubmissionModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        onSubmit={submitRequest}
        categories={categories}
      />
    </div>
  );
};
